// useAppAuth.js

import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

import axios from "axios";

import {
  useNavigate,
} from "react-router-dom";

import {
  getCurrentUser,
} from "../components/auth";

import {
  performLogout,
} from "../components/Logout";

import {
  closeSocket,
} from "../components/socketManager";

import {
  clearAllIDB,
} from "../db/fsDB";

export function useAppAuth() {

  const navigate =
    useNavigate();

  const [
    currentUser,
    setCurrentUser,
  ] = useState(null);

  const [
    authChecked,
    setAuthChecked,
  ] = useState(false);

  const [
    authScreen,
    setAuthScreen,
  ] = useState("login");

  /*
   * TRUE ONLY on explicit login.
   * FALSE on refresh/offline restore.
   */
  const justLoggedInRef =
    useRef(false);

  /*
   * Mirrors currentUser so the axios interceptor / timers
   * always see the latest value without re-subscribing.
   */
  const currentUserRef =
    useRef(null);

  useEffect(() => {

    currentUserRef.current =
      currentUser;

  }, [currentUser]);

  /*
   * GroupChat assigns its full handleLogout here
   * (it owns all the state setters).
   */
  const sessionExpiredHandlerRef =
    useRef(null);

  const expiringRef =
    useRef(false);

  /* =========================================================
     SESSION EXPIRED
     Token (5 days) ran out -> same cleanup as a manual
     logout: IDB (messages, lists, posts...), sockets,
     E2EE password, in-memory state, login screen.
  ========================================================= */

  const expireSession =
    useCallback(async () => {

      if (expiringRef.current) {
        return;
      }

      expiringRef.current = true;

      /* block re-entry from any in-flight 401s */
      currentUserRef.current = null;

      try {

        if (
          sessionExpiredHandlerRef.current
        ) {

          await sessionExpiredHandlerRef
            .current();

        } else {

          /* fallback if handler not wired yet */

          const chatId =
            currentUserRef.current
              ?.chatInfo?.[0]?.chatId ||
            currentUserRef.current
              ?.chatId ||
            null;

          closeSocket(chatId);

          try {

            await clearAllIDB();

          } catch (err) {

          }

          window.__E2EE_PASSWORD__ =
            null;

          setCurrentUser(null);

          setAuthChecked(true);

        }

      } finally {

        expiringRef.current = false;

      }

    }, []);

  /*
   * Asks the server whether the session is still valid.
   * Only expires on a DEFINITE answer (null user or 401/403)
   * while online, so offline use never wipes the IDB.
   */
  const verifySession =
    useCallback(async () => {

      if (
        !currentUserRef.current ||
        expiringRef.current ||
        !navigator.onLine
      ) {
        return;
      }

      try {

        const user =
          await getCurrentUser();

        if (!user) {

          await expireSession();

        }

      } catch (err) {

        const status =
          err?.response?.status;

        if (
          status === 401 ||
          status === 403
        ) {

          await expireSession();

        }

      }

    }, [expireSession]);

  /* 401 from any API call -> confirm, then expire */

  useEffect(() => {

    const id =
      axios.interceptors.response.use(

        (response) => response,

        (error) => {

          const status =
            error?.response?.status;

          const url =
            String(
              error?.config?.url || ""
            );

          if (
            status === 401 &&
            currentUserRef.current &&
            !url.includes("/api/login") &&
            !url.includes("/api/logout") &&
            !url.includes("/api/auth/me")
          ) {

            verifySession();

          }

          return Promise.reject(error);

        }

      );

    return () => {

      axios.interceptors.response
        .eject(id);

    };

  }, [verifySession]);

  /* Periodic + on-return-to-app check (covers idle tabs) */

  useEffect(() => {

    if (!currentUser) {
      return;
    }

    const onVisible = () => {

      if (
        document.visibilityState ===
        "visible"
      ) {

        verifySession();

      }

    };

    const interval =
      setInterval(
        verifySession,
        5 * 60 * 1000
      );

    document.addEventListener(
      "visibilitychange",
      onVisible
    );

    window.addEventListener(
      "online",
      verifySession
    );

    return () => {

      clearInterval(interval);

      document.removeEventListener(
        "visibilitychange",
        onVisible
      );

      window.removeEventListener(
        "online",
        verifySession
      );

    };

  }, [currentUser, verifySession]);

  /* =========================================================
     INITIAL AUTH CHECK
     ONLINE FIRST
     OFFLINE FALLBACK SECOND
  ========================================================= */

  useEffect(() => {

    let cancelled = false;

    (async () => {

      try {

        const user =
          await getCurrentUser();

        if (cancelled) {
          return;
        }

        if (!user) {

          /*
           * Session gone (expired token) while ONLINE:
           * wipe leftover messages from the IDB.
           * Offline -> keep data (offline restore).
           */
          if (navigator.onLine) {

            try {

              await clearAllIDB();

              window.__E2EE_PASSWORD__ =
                null;

            } catch (e) {

            }

          }

          return;

        }

        setCurrentUser(user);

      } catch (err) {

        const status =
          err?.response?.status;

        if (
          status === 401 ||
          status === 403
        ) {

          try {

            await clearAllIDB();

            window.__E2EE_PASSWORD__ =
              null;

          } catch (e) {

          }

        }

        setCurrentUser(null);

      } finally {

        if (!cancelled) {

          setAuthChecked(true);

        }

      }

    })();

    return () => {

      cancelled = true;

    };

  }, []);

  /* =========================================================
     LOGIN SUCCESS
  ========================================================= */

  const handleLoginSuccess =
    async (mode) => {

      if (mode === "account") {

        setAuthScreen("account");

        return;

      }

      if (mode === "forgot") {

        setAuthScreen("forgot");

        return;

      }

      /*
       * Explicit fresh login
       */

      justLoggedInRef.current =
        true;

      try {

        const user =
          await getCurrentUser();

        if (!user) {

          throw new Error(
            "No user returned"
          );

        }

        setCurrentUser(user);

        setAuthChecked(true);

        return user;

      } catch (err) {

        throw err;

      }

    };

  /* =========================================================
     LOGOUT
  ========================================================= */

  const handleLogout = async ({

    setUsers,
    setGroups,
    setMessages,

    setFeedPosts,
    setGlobalLivePosts,
    setInteractionPosts,

    setCommentsByPost,
    setNotificationCounts,

    setInteractionCounts,
    setInteractionActivity,

    setUswdWallet,
    setIsApprovedSeller,
    setUswdWalletLoading,
    setUswdWalletError,

    notificationHistoryLoadedRef,

    idbHydratedRef,
    hydrationSentRef,
    statusHydratedRef,
    setBootstrapDone,
    setUsersReady,
    setGroupsReady,

    feedHydratedRef,
    setFeedBootstrapDone,

    onBeforeLogout,

    setP2pOffers,
    setP2POrders,

    setSellerCurrency,
    setSellerPaymentDetails,
    setSellerPaymentCache,

  } = {}) => {

    onBeforeLogout?.();

    const chatId =
      currentUser?.chatInfo?.[0]?.chatId ||
      currentUser?.chatId ||
      null;

    closeSocket(chatId);

    /* =========================================
       ALWAYS CLEAR IDB ON LOGOUT
    ========================================= */

    try {

      await clearAllIDB();

      window.__E2EE_PASSWORD__ = null;

    } catch (err) {

    }

    /* =========================================
       SERVER LOGOUT
    ========================================= */

    await performLogout({

      setIsLoggedIn: () => {},

      navigate,

      redirectTo: "/",

    });

    /* =========================================
       RESET STATE
    ========================================= */

    setCurrentUser(null);

    setAuthChecked(true);

    setUsers?.([]);

    setGroups?.([]);

    setMessages?.({});

    setFeedPosts?.([]);

    setGlobalLivePosts?.([]);

    setInteractionPosts?.([]);

    setCommentsByPost?.({});

    setNotificationCounts?.([]);

    setInteractionCounts?.({});

    setInteractionActivity?.({});

    setUswdWallet?.(null);

    setIsApprovedSeller?.(false);

    setUswdWalletLoading?.(true);

    setUswdWalletError?.(null);

    setUsersReady?.(false);

    setGroupsReady?.(false);

    setP2pOffers?.([]);

    setP2POrders?.([]);

    setSellerCurrency?.(null);

    setSellerPaymentDetails?.([]);

    setSellerPaymentCache?.({});

    justLoggedInRef.current =
      false;

    if (notificationHistoryLoadedRef) {

      notificationHistoryLoadedRef.current =
        false;

    }

    if (statusHydratedRef) {

      statusHydratedRef.current =
        false;

    }

    if (idbHydratedRef) {

      idbHydratedRef.current =
        false;

      if (hydrationSentRef) {

        hydrationSentRef.current =
          false;

      }

    }

    setBootstrapDone?.(false);

    if (feedHydratedRef) {

      feedHydratedRef.current =
        false;

    }

    setFeedBootstrapDone?.(false);

  };

  return {

    currentUser,
    setCurrentUser,

    authChecked,

    authScreen,
    setAuthScreen,

    handleLoginSuccess,
    handleLogout,

    sessionExpiredHandlerRef,

    justLoggedInRef,

  };

}